/**
 * KT-241 — real delivery cost, shown in the cart, before checkout.
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 *
 * The team asked for the Victorian Plumbing layout: postage revealed right
 * after the postcode, on the checkout page. Checkout UI extensions for the
 * information/shipping/payment steps are Shopify Plus only and Kent Traders is
 * on Basic, so that exact layout is not available. This does the thing the
 * request was actually for, and does it one step EARLIER: the real figure is on
 * the cart page, before the customer types an address or sees a payment field.
 *
 * WHY WE ASK FOR A POSTCODE (and why we never invent one)
 *
 * Because /cart/shipping_rates.json is not a read. Shopify stores the address it
 * is given on the cart, and checkout pre-fills its postcode and province from
 * that store. The first cut of this component sent the SHOP's own postcode so it
 * could quote a rate without asking — and thereby wrote DA1 2SR into the
 * checkout of every signed-out shopper. Three orders shipped to Dartford instead
 * of the customer (2026-08-24 to 2026-09-17) before it was traced back to here.
 *
 * The address cannot be withdrawn once written: a 422 from the rates endpoint
 * leaves the previous one in place, and /cart/clear.js does not clear it. So the
 * rule this file exists to enforce is narrow and absolute — NEVER build a rates
 * URL from a postcode the shopper did not give us. shippingRatesUrl() returns
 * null without one and refresh() shows the prompt instead of fetching.
 *
 * WHY THE POSTCODE STILL DOES NOT CHANGE THE PRICE
 *
 * Measured 2026-08-19 against the live store. All six delivery profiles
 * (General, Corby & Grohe, Astroflame, Safelincs, RugDoctor, "Standard paid
 * rates") carry exactly ONE zone each, covering GB with all five provinces
 * (England, Scotland, Wales, Northern Ireland, British Forces). There is no
 * province split and no postcode-scoped zone anywhere. Confirmed empirically by
 * asking /cart/shipping_rates.json for nine real postcode areas — ME14, IV27
 * (Sutherland), BT1 (Belfast), ZE1 (Shetland), HS1 (Outer Hebrides), TR21
 * (Isles of Scilly), KW1, EH1, CF10 — and getting an identical rate table every
 * time. The postcode does not change the price, so asking for one would be
 * friction that buys the customer nothing.
 *
 * scripts/validate-delivery-zone-assumption.cjs asserts that invariant against
 * the Admin API and fails loudly the day someone adds a Highlands surcharge
 * zone. If that check ever fails, this component must start asking for a
 * postcode. Do not delete the check.
 *
 * WHY WE ASK SHOPIFY INSTEAD OF DOING THE MATHS
 *
 * Because the maths is not what anyone assumes. Measured, same date:
 *
 *   - Shopify offers only the rate NAMES that exist in EVERY delivery profile
 *     present in the cart, priced as the SUM of each profile's rate for that
 *     name. BG "Standard" 3.95 + Safelincs "Standard" 6.19 = 10.14, and the
 *     Express/Next-day options disappear entirely because Safelincs has none.
 *   - A basket of one 3.76 light switch plus one 1.15 fire strip offers exactly
 *     one option: Express at 24.89 (6.89 + 18.00), because "Express" is the only
 *     name the General and Astroflame profiles share.
 *   - The free-over-50 condition is evaluated against the WHOLE cart subtotal,
 *     not the General-profile subtotal.
 *
 * No Liquid mirror of that is going to stay correct. The storefront shipping
 * rates endpoint returns exactly what checkout will charge, so it is the only
 * honest source. This file formats what Shopify says and does no pricing maths
 * of its own beyond adding the cart total to the chosen rate — a sum verified
 * exact against draftOrderCalculate (subtotal 3.76 + shipping 3.95 = total 7.71,
 * tax inclusive).
 *
 * FAILURE POSTURE
 *
 * Any error, any non-200, any empty rate list: fall back to the pre-existing
 * "calculated at checkout" wording. Never render a number we are not certain
 * of. A vague cart is a bad cart; a confidently wrong cart is a complaint.
 */
(function () {
  'use strict';

  // Both sections/main-cart.liquid and sections/mini_cart.liquid render the
  // snippet, but never on the same page: render_bottom.liquid gates the drawer on
  // `page_type != 'cart'`, so /cart has exactly one root and every other page has
  // exactly one root. (An earlier note here claimed both render on /cart — measured
  // 2026-08-19, they do not.)
  //
  // The guard is NOT hypothetical insurance — it is load-bearing today. The drawer is
  // client-injected, and jQuery inserts the section's <script src> tags via globalEval, so
  // they EXECUTE AGAIN on every re-injection. Measured 2026-08-20: cart-drawer-fix.js was
  // fetched twice on a single page, which is that re-execution happening. Identical <link>
  // tags are deduplicated by the browser; identical <script src> tags are not.
  //
  // Without this, each drawer re-render would add another set of listeners and another rate
  // request per cart change — the same compounding-listener shape as the #370–#373 refresh
  // loop, arrived at from a different direction.
  if (window.__ktDeliveryCostInit) return;
  window.__ktDeliveryCostInit = true;

  var ROOT_SELECTOR = '[data-kt-delivery-cost]';
  var CART_EVENTS = ['cart:updated', 'cart:refresh', 'cart:update-display'];

  /** Requests in flight, keyed by root element, so a fast quantity-spinner
   *  cannot land an old answer on top of a new one. */
  var inflight = new WeakMap();

  /**
   * Shopify returns rate prices as a decimal string in the shop currency
   * ("3.95"), while cart.js returns minor units (376). Everything below works in
   * minor units so the two can be added without float drift.
   */
  function ratePriceToMinorUnits(priceString) {
    var normalised = String(priceString == null ? '' : priceString).trim();
    if (!/^-?\d+(\.\d+)?$/.test(normalised)) return null;
    return Math.round(parseFloat(normalised) * 100);
  }

  function formatMoney(minorUnits, config) {
    try {
      return new Intl.NumberFormat(config.locale || 'en-GB', {
        style: 'currency',
        currency: config.currency || 'GBP'
      }).format(minorUnits / 100);
    } catch (err) {
      return (config.currency === 'GBP' ? '£' : '') + (minorUnits / 100).toFixed(2);
    }
  }

  function readConfig(root) {
    return {
      country: root.getAttribute('data-country') || 'United Kingdom',
      zip: root.getAttribute('data-zip') || '',
      currency: root.getAttribute('data-currency') || 'GBP',
      locale: root.getAttribute('data-locale') || 'en-GB',
      mixedOrigins: root.getAttribute('data-mixed-origins') === 'true',
      supplierOnly: root.getAttribute('data-supplier-only') === 'true',
      strings: {
        label: root.getAttribute('data-str-label') || 'Delivery',
        free: root.getAttribute('data-str-free') || 'FREE',
        note: root.getAttribute('data-str-note') || '',
        noteFree: root.getAttribute('data-str-note-free') || '',
        noteRestricted: root.getAttribute('data-str-note-restricted') || '',
        noteRestrictedGeneric: root.getAttribute('data-str-note-restricted-generic') || '',
        noteSupplier: root.getAttribute('data-str-note-supplier') || '',
        noteSupplierGeneric: root.getAttribute('data-str-note-supplier-generic') || '',
        totalLabel: root.getAttribute('data-str-total-label') || '',
        optionsLabel: root.getAttribute('data-str-options-label') || '',
        fallback: root.getAttribute('data-str-fallback') || '',
        prompt: root.getAttribute('data-str-prompt') || '',
        postcodeInvalid: root.getAttribute('data-str-postcode-invalid') || ''
      }
    };
  }

  function el(root, name) {
    return root.querySelector('[data-kt-delivery-' + name + ']');
  }

  function setState(root, state) {
    root.setAttribute('data-state', state);
  }

  /** Renders the "we could not work it out" posture. Deliberately reuses the
   *  wording the cart already used before this component existed. */
  function renderFallback(root, config) {
    setState(root, 'unavailable');
    var amount = el(root, 'amount');
    var note = el(root, 'note');
    var options = el(root, 'options');
    var total = el(root, 'total');
    if (amount) amount.textContent = '';
    if (options) options.textContent = '';
    if (total) total.hidden = true;
    if (note) note.textContent = config.strings.fallback;
  }

  /** No postcode yet. Not an error state: the figure is unknown, not unavailable,
   *  and the form below is how the shopper resolves it. */
  function renderPrompt(root, config) {
    setState(root, 'prompt');
    var amount = el(root, 'amount');
    var note = el(root, 'note');
    var options = el(root, 'options');
    var total = el(root, 'total');
    if (amount) amount.textContent = '';
    if (options) options.textContent = '';
    if (total) total.hidden = true;
    if (note) note.textContent = config.strings.prompt || config.strings.fallback;
  }

  /**
   * Shopify labels a combined rate "Shipping" when the profiles in the cart
   * share no rate name, so the title is not a service the customer would
   * recognise. Naming it back at them ("Shipping is the only delivery service
   * available") reads like a bug.
   */
  function isGenericRateName(name) {
    return !name || /^shipping$/i.test(name);
  }

  function renderRates(root, config, rates, cartTotalMinorUnits) {
    var priced = [];
    for (var i = 0; i < rates.length; i++) {
      var minor = ratePriceToMinorUnits(rates[i].price);
      if (minor === null) continue;
      priced.push({ name: String(rates[i].name || '').trim(), minor: minor });
    }
    if (!priced.length) {
      renderFallback(root, config);
      return;
    }
    // If we dropped a rate we could not parse, the survivor is not evidence that
    // the basket is restricted — it is evidence that we failed to read one.
    var allRatesParsed = priced.length === rates.length;

    priced.sort(function (a, b) { return a.minor - b.minor; });
    var cheapest = priced[0];

    var amount = el(root, 'amount');
    if (amount) {
      amount.textContent = cheapest.minor === 0
        ? config.strings.free
        : formatMoney(cheapest.minor, config);
    }

    // The OTHER options, so the customer can see the upgrade prices. `priced` is
    // sorted ascending and `cheapest` is what the Delivery row above already
    // states, so it is dropped here: listing it again produced
    // "Delivery FREE / Also available: Free UK delivery FREE · Standard £3.95 …",
    // which reads as if the free rate were an alternative to itself.
    var options = el(root, 'options');
    if (options) {
      var alternatives = priced.slice(1);
      if (alternatives.length > 0) {
        options.textContent = config.strings.optionsLabel + ' ' + alternatives.map(function (r) {
          return r.name + ' ' + (r.minor === 0 ? config.strings.free : formatMoney(r.minor, config));
        }).join(' · ');
        options.hidden = false;
      } else {
        options.hidden = true;
      }
    }

    // A single option is only evidence of a restricted mixed-profile basket
    // when Liquid confirms that both fulfilment origins are present. A
    // supplier-only profile can legitimately expose one option as well; calling
    // that basket "mixed" is inaccurate and was caught in localhost acceptance.
    var note = el(root, 'note');
    if (note) {
      var singlePaidOption = allRatesParsed && priced.length === 1 && cheapest.minor > 0;
      var restricted = singlePaidOption && config.mixedOrigins;
      if (singlePaidOption && config.supplierOnly) {
        note.textContent = isGenericRateName(cheapest.name)
          ? (config.strings.noteSupplierGeneric || config.strings.note)
          : (config.strings.noteSupplier
            ? config.strings.noteSupplier.replace('[option]', cheapest.name)
            : config.strings.note);
      } else if (cheapest.minor === 0) {
        // The cart says FREE but Shopify's checkout pre-selects a rate by its own
        // ordering, and on 2 Sep 2026 that was "Standard £3.95" for a £52 basket
        // with "Free UK delivery" sitting right above it. Say the quiet part
        // out loud so nobody pays £3.95 they were told they wouldn't.
        note.textContent = config.strings.noteFree || config.strings.note;
      } else if (!restricted) {
        note.textContent = config.strings.note;
      } else if (isGenericRateName(cheapest.name)) {
        note.textContent = config.strings.noteRestrictedGeneric || config.strings.note;
      } else {
        note.textContent = config.strings.noteRestricted
          ? config.strings.noteRestricted.replace('[option]', cheapest.name)
          : config.strings.note;
      }
    }

    var total = el(root, 'total');
    var totalValue = el(root, 'total-value');
    if (total && totalValue && typeof cartTotalMinorUnits === 'number') {
      totalValue.textContent = formatMoney(cartTotalMinorUnits + cheapest.minor, config);
      total.hidden = false;
    } else if (total) {
      total.hidden = true;
    }

    setState(root, cheapest.minor === 0 ? 'free' : 'priced');
  }

  /**
   * UK postcode, loosely — enough to stop a typo being written to the cart as a
   * delivery address, not strict enough to reject a valid-but-unusual one. Shopify
   * is the real validator; this only decides whether it is worth asking.
   */
  var UK_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

  function normalisePostcode(value) {
    var raw = String(value == null ? '' : value).toUpperCase().replace(/\s+/g, '');
    if (!raw) return '';
    return raw.slice(0, -3) + ' ' + raw.slice(-3);
  }

  function isValidPostcode(value) {
    return UK_POSTCODE.test(String(value == null ? '' : value).trim());
  }

  /**
   * Returns null when we have no shopper-supplied postcode. Callers MUST treat null
   * as "do not fetch" rather than falling back to a default — see the header. This
   * is the single choke point through which a delivery address can reach the cart.
   */
  function shippingRatesUrl(config) {
    if (!config.zip || !isValidPostcode(config.zip)) return null;
    var params = new URLSearchParams();
    params.set('shipping_address[country]', config.country);
    params.set('shipping_address[zip]', config.zip);
    return '/cart/shipping_rates.json?' + params.toString();
  }

  /**
   * Remembered for THIS SESSION only, so a shopper who typed a postcode on the cart
   * page is not asked again in the drawer thirty seconds later.
   *
   * sessionStorage, deliberately, not localStorage. Anything remembered here is fired
   * at /cart/shipping_rates.json on the next cart view without the shopper touching
   * anything — and that request WRITES the address onto the cart and into checkout.
   * A postcode from a previous visit is not necessarily this shopper's: a shared
   * family device, a gift sent to someone else last month, a colleague's order on the
   * trade desk. Auto-firing that would rebuild the original incident with a stale
   * postcode instead of the store's. Session scope keeps "the shopper typed it" and
   * "this session" the same fact. Review finding, 2026-09-18.
   *
   * Storage can throw in private mode; every access is guarded.
   */
  var STORAGE_KEY = 'kt:delivery-postcode';

  function readStoredPostcode() {
    try {
      var stored = window.sessionStorage.getItem(STORAGE_KEY);
      return stored && isValidPostcode(stored) ? normalisePostcode(stored) : '';
    } catch (err) { return ''; }
  }

  function storePostcode(postcode) {
    try { window.sessionStorage.setItem(STORAGE_KEY, postcode); } catch (err) { /* private mode */ }
  }

  function refresh(root, force) {
    var config = readConfig(root);

    var previous = inflight.get(root);
    if (previous) previous.abort();
    var controller = new AbortController();
    inflight.set(root, controller);

    setState(root, 'loading');

    // cart.js rather than the rendered DOM: the drawer and the cart page can
    // both be stale mid-update, and the total we print has to be the one
    // checkout will use.
    fetch('/cart.js', { signal: controller.signal, headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('cart ' + res.status);
        return res.json();
      })
      .then(function (cart) {
        if (!cart || !cart.item_count) {
          root.hidden = true;
          setState(root, 'empty');
          return null;
        }
        root.hidden = false;

        var key = [cart.token, cart.item_count, cart.total_price].join(':');
        if (!force && root.getAttribute('data-cart-key') === key &&
            root.getAttribute('data-state') !== 'loading') {
          return null;
        }
        root.setAttribute('data-cart-key', key);

        // The choke point. No shopper-supplied postcode means no request, because a
        // request is a WRITE of a delivery address onto this cart. See the header.
        var url = shippingRatesUrl(config);
        if (!url) {
          root.removeAttribute('data-cart-key');
          renderPrompt(root, config);
          return null;
        }

        return fetch(url, {
          signal: controller.signal,
          headers: { Accept: 'application/json' }
        }).then(function (res) {
          if (!res.ok) throw new Error('rates ' + res.status);
          return res.json();
        }).then(function (body) {
          if (!body || !Array.isArray(body.shipping_rates)) throw new Error('rates shape');
          renderRates(root, config, body.shipping_rates, cart.total_price);
          return null;
        });
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        // Clear the key so the next cart event retries rather than trusting a
        // failed read.
        root.removeAttribute('data-cart-key');
        renderFallback(root, config);
      })
      .then(function () {
        if (inflight.get(root) === controller) inflight.delete(root);
      });
  }

  function refreshAll(force) {
    var roots = document.querySelectorAll(ROOT_SELECTOR);
    for (var i = 0; i < roots.length; i++) refresh(roots[i], force);
  }

  /**
   * Applying the typed postcode is the only way a zip enters data-zip after render.
   *
   * Driven by click and by Enter, NOT by a submit event: this component renders inside
   * the cart's own <form>, so it cannot contain a form of its own (see the comment in
   * snippets/kt-delivery-cost.liquid). Enter is intercepted here precisely because the
   * enclosing form would otherwise post the cart and reload the page.
   */
  function applyPostcode(trigger) {
    var container = trigger.closest && trigger.closest('[data-kt-delivery-postcode-form]');
    if (!container) return;

    var root = container.closest(ROOT_SELECTOR);
    if (!root) return;

    var config = readConfig(root);
    var input = el(root, 'postcode-input');
    var error = el(root, 'postcode-error');
    var entered = input ? input.value : '';

    if (!isValidPostcode(entered)) {
      if (error) {
        error.textContent = config.strings.postcodeInvalid;
        error.hidden = false;
      }
      if (input) input.focus();
      return;
    }

    var postcode = normalisePostcode(entered);
    if (error) { error.textContent = ''; error.hidden = true; }
    if (input) input.value = postcode;
    storePostcode(postcode);
    root.setAttribute('data-zip', postcode);
    refresh(root, true);
  }

  /** A signed-out shopper who gave us a postcode earlier in this session should not
   *  be asked again. Only ever their own value, and only when Liquid left it blank. */
  function seedFromStorage(root) {
    if (root.getAttribute('data-zip')) return;
    var stored = readStoredPostcode();
    if (!stored) return;
    root.setAttribute('data-zip', stored);
    var input = el(root, 'postcode-input');
    if (input && !input.value) input.value = stored;
  }

  function seedAll() {
    var roots = document.querySelectorAll(ROOT_SELECTOR);
    for (var i = 0; i < roots.length; i++) seedFromStorage(roots[i]);
  }

  function init() {
    // Listeners first, unconditionally. The one-shot guard at the top of this file has
    // already fired by now, so if this returned early on "no root yet" (a drawer
    // injected later, or a script tag that ran before its markup) the click/Enter
    // handlers would never be bound for the life of the page — and Enter in the
    // postcode box would fall through to the cart form and submit it. The refresh is
    // the only part that needs a root to exist.
    CART_EVENTS.forEach(function (name) {
      document.addEventListener(name, function () { seedAll(); refreshAll(true); });
    });
    // Delegated once on the document: the drawer is injected after load, and re-binding
    // per render is how the #370 listener pile-up happened.
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest && event.target.closest('[data-kt-delivery-postcode-submit]');
      if (!trigger) return;
      event.preventDefault();
      applyPostcode(trigger);
    });
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return;
      var input = event.target.closest && event.target.closest('[data-kt-delivery-postcode-input]');
      if (!input) return;
      // Without this the enclosing cart form submits and the page reloads.
      event.preventDefault();
      applyPostcode(input);
    });

    if (!document.querySelector(ROOT_SELECTOR)) return;
    seedAll();
    refreshAll(false);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The drawer can be injected after first paint. One observer, scoped to added
  // nodes, is cheaper than polling and cheaper than re-running init on a timer.
  if (window.MutationObserver) {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(ROOT_SELECTOR)) { seedFromStorage(node); refresh(node, true); continue; }
          if (node.querySelector && node.querySelector(ROOT_SELECTOR)) { seedAll(); refreshAll(true); }
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
